import React, { useState, useEffect } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const OrderDialog = ({ item, onClose }) => {
  // Load client info from localStorage if available
  const getStoredClientInfo = () => {
    const storedInfo = localStorage.getItem('clientInfo');
    return storedInfo ? JSON.parse(storedInfo) : { name: "", phone: "", address: "" };
  };

  const [name, setName] = useState(getStoredClientInfo().name);
  const [phone, setPhone] = useState(getStoredClientInfo().phone);
  const [address, setAddress] = useState(getStoredClientInfo().address);
  const [quantity, setQuantity] = useState(1);
  const [selectedForm, setSelectedForm] = useState('powder'); // 'powder' or 'grains' for fish food
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const normalizeSelection = (selection = {}) => ({
    MALE: selection.MALE || 0,
    FEMALE: selection.FEMALE || 0,
    COUPLE: selection.COUPLE || 0,
  });

  const enableMultiSelection = Boolean(item?.selectedQuantities);
  const [selectionQuantities, setSelectionQuantities] = useState(
    enableMultiSelection ? normalizeSelection(item.selectedQuantities) : null
  );

  useEffect(() => {
    if (enableMultiSelection) {
      setSelectionQuantities(normalizeSelection(item.selectedQuantities));
    } else {
      setSelectionQuantities(null);
    }
  }, [enableMultiSelection, item.selectedQuantities]);

  // Save client info to localStorage whenever it changes
  useEffect(() => {
    const clientInfo = { name, phone, address };
    localStorage.setItem('clientInfo', JSON.stringify(clientInfo));
  }, [name, phone, address]);

  // Determine item type and handle different item structures
  const getItemType = () => {
    if (item.type === "plant") return "plant";
    if (item.type === "aquarium") return "aquarium";
    if (item.type === "food") return "food";
    if (item.guppynumber !== undefined) return "guppy";
    return "unknown";
  };

  const itemType = getItemType();

  // Check if this is fish food (has basePricePer50g or description with fish food info)
  const isFishFood = itemType === "food" && (
    item.basePricePer50g ||
    item.description?.includes('مسحوق سمك') ||
    item.name?.toLowerCase().includes('سمك') ||
    item.name?.toLowerCase().includes('fish food') ||
    item.basePricePer50g === 500 // Default fish food price
  );

  // Initialize quantity for fish food (50g = 1 unit)
  useEffect(() => {
    if (isFishFood) {
      setQuantity(1); // Start with 50g (1 unit = 50g)
      setSelectedForm(item.selectedForm || 'powder');
    }
  }, [isFishFood, item.selectedForm]);

  // Check if fish is male, female or couple
  const getFishType = () => {
    if (item.singular) {
      if (item.maleprice > 0) return "male";
      if (item.femaleprice > 0) return "female";
    }
    return "couple";
  };

  const fishType = itemType === "guppy" ? getFishType() : null;

  // Calculate price based on item type with discount for 5+ couples
  const calculateTotalPrice = () => {
    if (enableMultiSelection && selectionQuantities) {
      const maleTotal = selectionQuantities.MALE * (item.maleprice || 0);
      const femaleTotal = selectionQuantities.FEMALE * (item.femaleprice || 0);
      const coupleSubtotal = selectionQuantities.COUPLE * (item.coupleprice || 0);
      const coupleDiscount = selectionQuantities.COUPLE >= 5 ? coupleSubtotal * 0.1 : 0;

      return maleTotal + femaleTotal + (coupleSubtotal - coupleDiscount);
    }

    let basePrice;
    switch (itemType) {
      case "plant":
        basePrice = item.price * quantity;
        break;

      case "aquarium":
        basePrice = item.price * quantity;
        break;

      case "food":
        if (isFishFood) {
          const basePricePer50g = item.basePricePer50g || 500;
          basePrice = basePricePer50g * quantity;
        } else {
          const foodType = item.selectedFoodType || selectedForm || 'flakes';
          basePrice = foodType === 'powder'
            ? item.powderPrice * quantity
            : item.flakesPrice * quantity;
        }
        break;

      case "guppy":
        if (fishType === "male") basePrice = item.maleprice * quantity;
        else if (fishType === "female") basePrice = item.femaleprice * quantity;
        else basePrice = item.coupleprice * quantity;
        break;

      default:
        basePrice = item.price * quantity;
    }

    if (itemType === "guppy" && fishType === "couple" && quantity >= 5) {
      return basePrice * 0.9;
    }

    return basePrice;
  };

  const totalPrice = calculateTotalPrice();

  const discountAmount = enableMultiSelection && selectionQuantities
    ? (selectionQuantities.COUPLE >= 5
      ? (selectionQuantities.COUPLE * (item.coupleprice || 0)) * 0.1
      : 0)
    : (itemType === "guppy" && fishType === "couple" && quantity >= 5
      ? (item.coupleprice * quantity) * 0.1
      : 0);

  const hasDiscount = enableMultiSelection
    ? Boolean(selectionQuantities && selectionQuantities.COUPLE >= 5)
    : (itemType === "guppy" && fishType === "couple" && quantity >= 5);

  // Get maximum available quantity based on item type
  const getMaxQuantity = () => {
    switch (itemType) {
      case "plant":
        return item.quantity || 100; // Default to 100 if quantity not specified

      case "aquarium":
        return item.quantity || 10; // Default to 10 if quantity not specified

      case "food":
        // Check if this is fish food with quantity-based pricing
        if (isFishFood) {
          return 1000; // High limit for fish food (can order up to 1000 units of 50g = 50kg)
        }
        // Regular food
        const foodType = item.selectedFoodType || selectedForm || 'flakes';
        if (foodType === 'powder') {
          return item.powderQuantity || 100;
        }
        return item.flakesQuantity || 100;

      case "guppy":
        if (fishType === "male") return item.malecount || 0;
        if (fishType === "female") return item.femalecount || 0;
        return item.couplecount || 0;

      default:
        return item.quantity || 1;
    }
  };

  const maxQuantity = getMaxQuantity();
  const totalSelectedItems = selectionQuantities
    ? selectionQuantities.MALE + selectionQuantities.FEMALE + selectionQuantities.COUPLE
    : 0;
  const multiSelectionOptions = {
    MALE: {
      label: "ذكور",
      icon: "♂️",
      price: item.maleprice || 0,
      available: item.malecount || 0,
      color: "#06b6d4",
    },
    FEMALE: {
      label: "إناث",
      icon: "♀️",
      price: item.femaleprice || 0,
      available: item.femalecount || 0,
      color: "#ec4899",
    },
    COUPLE: {
      label: "أزواج",
      icon: "💕",
      price: item.coupleprice || 0,
      available: item.couplecount || 0,
      color: "#8b5cf6",
    },
  };
  const adjustSelectionQuantity = (key, delta) => {
    if (!selectionQuantities) return;
    const option = multiSelectionOptions[key];
    if (!option) return;

    setSelectionQuantities((prev) => {
      const current = prev?.[key] || 0;
      const next = Math.min(
        Math.max(current + delta, 0),
        option.available
      );

      return {
        ...prev,
        [key]: next,
      };
    });
  };

  // Create order data based on item type
  const createOrderData = () => {
    const baseOrderData = {
      totalPrice: totalPrice,
      customerName: name,
      customerPhone: phone,
      customerAddress: address,
      orderDate: serverTimestamp(),
      status: "Pending",
      hasDiscount: hasDiscount,
    };

    switch (itemType) {
      case "plant":
        return {
          ...baseOrderData,
          items: [{
            itemId: item.id, // Store ID for reliable lookup
            type: "plant",
            name: item.name,
            price: item.price,
            quantity: quantity,
            care: item.care,
            plantType: item.type,
            tags: item.tags
          }]
        };

      case "aquarium":
        return {
          ...baseOrderData,
          items: [{
            itemId: item.id, // Store ID for reliable lookup
            type: "aquarium",
            name: item.name,
            price: item.price,
            quantity: quantity,
            size: item.size,
            material: item.material
          }]
        };

      case "food":
        // Check if this is fish food with quantity-based pricing
        if (isFishFood) {
          const basePricePer50g = item.basePricePer50g || 500;
          const weightInGrams = quantity * 50; // quantity is in units of 50g
          return {
            ...baseOrderData,
            items: [{
              itemId: item.id, // Store ID for reliable lookup
              type: "food",
              name: item.name || "طعام الأسماك المميز",
              foodType: selectedForm, // 'powder' or 'grains'
              form: selectedForm === 'powder' ? 'مسحوق' : 'حبات صغار (1ملم)',
              basePricePer50g: basePricePer50g,
              quantity: quantity, // units of 50g
              weightInGrams: weightInGrams,
              price: basePricePer50g * quantity, // total price
              description: item.description,
              benefits: item.benefits,
              isFishFood: true
            }]
          };
        }
        // Regular food
        const foodType = item.selectedFoodType || selectedForm || 'flakes';
        return {
          ...baseOrderData,
          items: [{
            itemId: item.id, // Store ID for reliable lookup
            type: "food",
            name: item.name,
            foodType: foodType,
            price: foodType === 'powder' ? item.powderPrice : item.flakesPrice,
            quantity: quantity,
            flakesPrice: item.flakesPrice,
            powderPrice: item.powderPrice,
            flakesQuantity: item.flakesQuantity,
            powderQuantity: item.powderQuantity
          }]
        };

      case "guppy":
        if (enableMultiSelection && selectionQuantities) {
          const selectionItems = [];
          if (selectionQuantities.MALE > 0) {
            selectionItems.push({
              itemId: item.id, // Store ID for reliable lookup
              type: "guppy",
              guppyName: item.name,
              guppyNumber: item.guppynumber,
              malePrice: item.maleprice,
              maleCount: selectionQuantities.MALE,
              totalPrice: selectionQuantities.MALE * (item.maleprice || 0),
            });
          }
          if (selectionQuantities.FEMALE > 0) {
            selectionItems.push({
              itemId: item.id, // Store ID for reliable lookup
              type: "guppy",
              guppyName: item.name,
              guppyNumber: item.guppynumber,
              femalePrice: item.femaleprice,
              femaleCount: selectionQuantities.FEMALE,
              totalPrice: selectionQuantities.FEMALE * (item.femaleprice || 0),
            });
          }
          if (selectionQuantities.COUPLE > 0) {
            const coupleSubtotal = selectionQuantities.COUPLE * (item.coupleprice || 0);
            selectionItems.push({
              itemId: item.id, // Store ID for reliable lookup
              type: "guppy",
              guppyName: item.name,
              guppyNumber: item.guppynumber,
              couplePrice: item.coupleprice,
              coupleCount: selectionQuantities.COUPLE,
              originalPrice: coupleSubtotal,
              discount: hasDiscount ? coupleSubtotal * 0.1 : 0,
              totalPrice: hasDiscount ? coupleSubtotal * 0.9 : coupleSubtotal,
            });
          }

          return {
            ...baseOrderData,
            hasDiscount,
            items: selectionItems,
            selectionSummary: selectionQuantities,
            totalSelectedItems,
            totalPrice,
          };
        }

        const guppyItem = {
          itemId: item.id, // Store ID for reliable lookup
          type: "guppy",
          guppyName: item.name,
          guppyNumber: item.guppynumber,
          totalPrice: totalPrice,
          hasDiscount: hasDiscount,
        };

        if (fishType === "male") {
          return {
            ...baseOrderData,
            items: [{
              ...guppyItem,
              malePrice: item.maleprice,
              maleCount: quantity
            }]
          };
        } else if (fishType === "female") {
          return {
            ...baseOrderData,
            items: [{
              ...guppyItem,
              femalePrice: item.femaleprice,
              femaleCount: quantity
            }]
          };
        } else {
          return {
            ...baseOrderData,
            items: [{
              ...guppyItem,
              couplePrice: item.coupleprice,
              coupleCount: quantity,
              originalPrice: item.coupleprice * quantity,
              discount: hasDiscount ? (item.coupleprice * quantity * 0.1) : 0
            }]
          };
        }

      default:
        return {
          ...baseOrderData,
          items: [{
            itemId: item.id, // Store ID for reliable lookup
            type: "generic",
            name: item.name,
            price: item.price,
            quantity: quantity
          }]
        };
    }
  };

  const handlePlaceOrder = async () => {
    if (!name || !phone || !address) {
      setError("Please fill out all fields.");
      return;
    }

    if (enableMultiSelection) {
      if (totalSelectedItems === 0) {
        setError("يرجى اختيار كمية واحدة على الأقل.");
        return;
      }
      const exceededEntry = Object.entries(selectionQuantities || {}).find(([key, count]) => {
        const option = multiSelectionOptions[key];
        return count > (option?.available ?? 0);
      });
      if (exceededEntry) {
        setError(`الكمية المتاحة فقط ${(multiSelectionOptions[exceededEntry[0]]?.available ?? 0)} لهذا الصنف.`);
        return;
      }
    } else {
      if (quantity < 1) {
        setError("الكمية يجب أن تكون على الأقل 1");
        return;
      }

      if (quantity > maxQuantity) {
        setError(`الكمية المتاحة فقط ${maxQuantity}`);
        return;
      }
    }

    setLoading(true);
    setError("");

    try {
      const order = createOrderData();
      // Add serverTimestamp manually if needed by n8n or let it pass through (it's a Firebase object)
      const docRef = await addDoc(collection(db, "orders"), order);
      const orderId = docRef.id;

      try {
        // Prepare order data for webhook (converting Firestore timestamp if necessary, though n8n can handle JSON)
        const webhookData = {
          ...order,
          orderId,
          // remove the firebase FieldValue timestamp for the webhook if it causes JSON serialization issues, 
          // or replace it with a standard ISO string
          orderDate: new Date().toISOString()
        };

        const webhookUrl = process.env.REACT_APP_N8N_NEW_ORDER_WEBHOOK_URL;
        await fetch(webhookUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(webhookData),
        });
        console.log("Order sent to n8n webhook successfully");
      } catch (webhookError) {
        console.error("Failed to send order to n8n webhook:", webhookError);
        // We do not fail the overall order placement if the webhook fails
      }

      // Store the order ID in localStorage
      const orderIdsJson = localStorage.getItem('orderIds') ?? '[]';
      const orderIds = JSON.parse(orderIdsJson);
      orderIds.push(orderId);
      localStorage.setItem('orderIds', JSON.stringify(orderIds));

      console.log("Order ID:", orderId);
      alert("تم تقديم طلبك بنجاح. سنتواصل معك قريبًا");
      onClose();
    } catch (error) {
      console.error("Error placing order:", error);
      setError("حدث خطأ أثناء تقديم طلبك. يرجى المحاولة مرة أخرى");
    } finally {
      setLoading(false);
    }
  };

  // Render item details based on type
  const renderItemDetails = () => {
    switch (itemType) {
      case "plant":
        return (
          <>
            <div className="detail-row">
              <span className="detail-label">الاسم: </span>
              <span className="detail-value">{item.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">النوع: </span>
              <span className="detail-value">{item.type || "نبات"}</span>
            </div>
            {item.care && (
              <div className="detail-row">
                <span className="detail-label">العناية: </span>
                <span className="detail-value">{item.care}</span>
              </div>
            )}
            {item.tags && (
              <div className="detail-row">
                <span className="detail-label">عدد الفروع: </span>
                <span className="detail-value">{item.tags}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">السعر للوحدة: </span>
              <span className="detail-value price-highlight">{item.price} Dzd</span>
            </div>
            <div className="quantity-control">
              <span className="detail-label">الكمية: </span>
              <div className="quantity-buttons">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="quantity-btn"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-display">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                  className="quantity-btn"
                  disabled={quantity >= maxQuantity}
                >
                  +
                </button>
              </div>
              {maxQuantity && (
                <div className="available-quantity">
                  المتاح: {maxQuantity}
                </div>
              )}
            </div>
          </>
        );

      case "aquarium":
        return (
          <>
            <div className="detail-row">
              <span className="detail-label">الاسم: </span>
              <span className="detail-value">{item.name}</span>
            </div>
            {item.size && (
              <div className="detail-row">
                <span className="detail-label">الحجم: </span>
                <span className="detail-value">{item.size}</span>
              </div>
            )}
            {item.material && (
              <div className="detail-row">
                <span className="detail-label">المادة: </span>
                <span className="detail-value">{item.material}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">السعر للوحدة: </span>
              <span className="detail-value price-highlight">{item.price} Dzd</span>
            </div>
            <div className="quantity-control">
              <span className="detail-label">الكمية: </span>
              <div className="quantity-buttons">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="quantity-btn"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-display">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                  className="quantity-btn"
                  disabled={quantity >= maxQuantity}
                >
                  +
                </button>
              </div>
              {maxQuantity && (
                <div className="available-quantity">
                  المتاح: {maxQuantity}
                </div>
              )}
            </div>
          </>
        );

      case "guppy":
        if (enableMultiSelection && selectionQuantities) {
          return (
            <>
              <div className="detail-row">
                <span className="detail-label">الاسم: </span>
                <span className="detail-value">{item.name}</span>
              </div>

              <div className="multi-selection-wrapper">
                {Object.entries(multiSelectionOptions).map(([key, option]) => (
                  <div
                    key={key}
                    className={`multi-selection-card ${option.available === 0 ? 'disabled' : ''}`}
                  >
                    <div className="multi-selection-header">
                      <span className="multi-selection-label">
                        {option.icon} {option.label}
                      </span>
                      <span className="multi-selection-price">
                        {option.price?.toLocaleString()} Dzd
                      </span>
                    </div>

                    <div className="multi-selection-controls">
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={() => adjustSelectionQuantity(key, -1)}
                        disabled={selectionQuantities[key] === 0}
                      >
                        -
                      </button>
                      <span className="multi-selection-value">
                        {selectionQuantities[key]}
                      </span>
                      <button
                        type="button"
                        className="quantity-btn"
                        onClick={() => adjustSelectionQuantity(key, 1)}
                        disabled={selectionQuantities[key] >= option.available}
                      >
                        +
                      </button>
                    </div>

                    <div className="available-quantity">
                      المتاح: {option.available}
                    </div>
                  </div>
                ))}
              </div>

              {selectionQuantities.COUPLE > 0 && selectionQuantities.COUPLE < 5 && (
                <div className="discount-message">
                  <span className="discount-icon">💎</span>
                  اطلب {5 - selectionQuantities.COUPLE} أزواج إضافية للحصول على خصم 10٪!
                </div>
              )}
              {hasDiscount && (
                <div className="discount-applied">
                  <span className="discount-icon">🎉</span>
                  تم تطبيق خصم 10٪ على الأزواج في طلبك
                </div>
              )}
            </>
          );
        }

        return (
          <>
            <div className="detail-row">
              <span className="detail-label">الاسم: </span>
              <span className="detail-value">{item.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">النوع: </span>
              <span className="detail-value">
                {fishType === "male" ? "ذكر" : fishType === "female" ? "أنثى" : "زوج"}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">السعر: </span>
              <span className="detail-value price-highlight">
                {fishType === "male" ? item.maleprice :
                  fishType === "female" ? item.femaleprice : item.coupleprice} Dzd
              </span>
            </div>
            {fishType === "couple" && quantity < 5 && (
              <div className="discount-message">
                <span className="discount-icon">💎</span>
                اطلب 5 أزواج أو أكثر واحصل على خصم 10٪!
              </div>
            )}
            {hasDiscount && (
              <div className="discount-applied">
                <span className="discount-icon">🎉</span>
                تم تطبيق خصم 10٪ على طلبك!
              </div>
            )}
            <div className="quantity-control">
              <span className="detail-label">الكمية: </span>
              <div className="quantity-buttons">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="quantity-btn"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-display">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                  className="quantity-btn"
                  disabled={quantity >= maxQuantity}
                >
                  +
                </button>
              </div>
              {maxQuantity > 0 && (
                <div className="available-quantity">
                  المتاح: {maxQuantity}
                </div>
              )}
            </div>
          </>
        );

      case "food":
        // Check if this is fish food
        if (isFishFood) {
          const basePricePer50g = item.basePricePer50g || 500;
          const weightInGrams = quantity * 50;
          return (
            <>
              <div className="detail-row">
                <span className="detail-label">الاسم: </span>
                <span className="detail-value">{item.name || "طعام الأسماك المميز"}</span>
              </div>

              {item.description && (
                <div className="detail-row">
                  <span className="detail-label">المكونات: </span>
                  <span className="detail-value" style={{ fontSize: '13px', color: '#94a3b8' }}>
                    {item.description}
                  </span>
                </div>
              )}

              {item.benefits && (
                <div className="detail-row">
                  <span className="detail-label">الفوائد: </span>
                  <span className="detail-value" style={{ fontSize: '13px', color: '#10b981' }}>
                    {item.benefits}
                  </span>
                </div>
              )}

              <div className="detail-row">
                <span className="detail-label">الشكل: </span>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => setSelectedForm('powder')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: selectedForm === 'powder' ? '2px solid #10b981' : '1px solid rgba(71, 85, 105, 0.5)',
                      background: selectedForm === 'powder' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(51, 65, 85, 0.5)',
                      color: selectedForm === 'powder' ? '#10b981' : 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: selectedForm === 'powder' ? '600' : '400'
                    }}
                  >
                    مسحوق (غبرة)
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectedForm('grains')}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '8px',
                      border: selectedForm === 'grains' ? '2px solid #10b981' : '1px solid rgba(71, 85, 105, 0.5)',
                      background: selectedForm === 'grains' ? 'rgba(16, 185, 129, 0.2)' : 'rgba(51, 65, 85, 0.5)',
                      color: selectedForm === 'grains' ? '#10b981' : 'white',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: selectedForm === 'grains' ? '600' : '400'
                    }}
                  >
                    حبات صغار (1 ملم)
                  </button>
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">السعر: </span>
                <span className="detail-value price-highlight">
                  {basePricePer50g} دج لكل 50غ
                </span>
              </div>

              <div className="quantity-control">
                <span className="detail-label">الكمية (كل وحدة = 50غ): </span>
                <div className="quantity-buttons">
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="quantity-btn"
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span className="quantity-display">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                    className="quantity-btn"
                    disabled={quantity >= maxQuantity}
                  >
                    +
                  </button>
                </div>
                <div style={{ marginTop: '8px', fontSize: '13px', color: '#94a3b8' }}>
                  الوزن الإجمالي: {weightInGrams} غ ({quantity} × 50غ)
                </div>
              </div>
            </>
          );
        }
        // Regular food rendering (existing logic)
        return (
          <>
            <div className="detail-row">
              <span className="detail-label">الاسم: </span>
              <span className="detail-value">{item.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">النوع: </span>
              <span className="detail-value">
                {item.selectedFoodType === 'powder' ? 'مسحوق' : 'رقائق'}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">السعر: </span>
              <span className="detail-value price-highlight">
                {item.selectedFoodType === 'powder' ? item.powderPrice : item.flakesPrice} Dzd
              </span>
            </div>
            <div className="quantity-control">
              <span className="detail-label">الكمية: </span>
              <div className="quantity-buttons">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="quantity-btn"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-display">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                  className="quantity-btn"
                  disabled={quantity >= maxQuantity}
                >
                  +
                </button>
              </div>
              {maxQuantity && (
                <div className="available-quantity">
                  المتاح: {maxQuantity}
                </div>
              )}
            </div>
          </>
        );

      default:
        return (
          <>
            <div className="detail-row">
              <span className="detail-label">الاسم: </span>
              <span className="detail-value">{item.name}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">السعر: </span>
              <span className="detail-value price-highlight">{item.price} Dzd</span>
            </div>
            <div className="quantity-control">
              <span className="detail-label">الكمية: </span>
              <div className="quantity-buttons">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="quantity-btn"
                  disabled={quantity <= 1}
                >
                  -
                </button>
                <span className="quantity-display">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity(Math.min(maxQuantity, quantity + 1))}
                  className="quantity-btn"
                  disabled={quantity >= maxQuantity}
                >
                  +
                </button>
              </div>
              {maxQuantity && (
                <div className="available-quantity">
                  المتاح: {maxQuantity}
                </div>
              )}
            </div>
          </>
        );
    }
  };

  return (
    <>
      <div className="order-overlay" onClick={onClose}>
        <div
          className="order-dialog"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header with animated icon */}
          <div className="dialog-header">
            <div className="header-icon">🛒</div>
            <h2 className="dialog-title">تأكيد الطلب</h2>
            <button
              className="close-btn"
              onClick={onClose}
              aria-label="إغلاق"
            >
              ✕
            </button>
          </div>

          {/* Item Details Card */}
          <div className="item-details-card">
            <div className="card-header">
              <span className="card-icon">📦</span>
              <h3 className="card-title">تفاصيل الطلب</h3>
            </div>

            <div className="details-content">
              {renderItemDetails()}
            </div>

            <div className="total-section">
              {hasDiscount && (
                <div className="original-price-row">
                  <span className="original-price-label">السعر الأصلي:</span>
                  <span className="original-price-amount">
                    {Math.round(totalPrice + discountAmount).toLocaleString()} Dzd
                  </span>
                </div>
              )}
              <div className="total-row">
                <span className="total-label">المجموع:</span>
                <span className="total-amount">{Math.round(totalPrice)} Dzd</span>
              </div>
              {hasDiscount && (
                <div className="discount-row">
                  <span className="discount-label">الخصم:</span>
                  <span className="discount-amount">
                    {Math.round(discountAmount)} Dzd
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Order Form */}
          <form className="order-form" onSubmit={(e) => {
            e.preventDefault();
            handlePlaceOrder();
          }}>
            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type="text"
                  placeholder="الاسم"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="form-input"
                  required
                />
                <span className="input-icon">👤</span>
              </div>
            </div>

            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type="tel"
                  placeholder="رقم الهاتف"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="form-input"
                  required
                />
                <span className="input-icon">📱</span>
              </div>
            </div>

            <div className="form-group">
              <div className="input-wrapper">
                <input
                  type="text"
                  placeholder="العنوان"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  className="form-input"
                  required
                />
                <span className="input-icon">📍</span>
              </div>
            </div>

            {error && (
              <div className="error-message">
                <span className="error-icon">⚠️</span>
                {error}
              </div>
            )}

            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={onClose}
                disabled={loading}
              >
                إلغاء
              </button>
              <button
                type="submit"
                className="submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="loading-spinner"></span>
                    جارٍ إرسال الطلب...
                  </>
                ) : (
                  <>
                    <span className="submit-icon">✨</span>
                    تأكيد الطلب
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Enhanced Styles */}
      <style>{`
        .order-overlay {
          position: fixed;
          inset: 0;
          z-index: 1000;
          background: rgba(0, 0, 0, 0.8);
          backdrop-filter: blur(8px);
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 16px;
          animation: fadeIn 0.3s ease-out;
        }

        .order-dialog {
          background: linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95));
          backdrop-filter: blur(20px);
          border: 1px solid rgba(16, 185, 129, 0.2);
          border-radius: 20px;
          width: 100%;
          max-width: 480px;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
          text-align: right;
          direction: rtl;
          position: relative;
          animation: slideUp 0.4s ease-out;
        }

        .dialog-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 24px 24px 16px;
          border-bottom: 1px solid rgba(51, 65, 85, 0.3);
          position: relative;
        }

        .header-icon {
          font-size: 2rem;
          animation: bounce 2s infinite;
        }

        .dialog-title {
          color: #ffffff;
          margin: 0;
          font-size: 1.5rem;
          font-weight: 700;
          background: linear-gradient(135deg, #10b981, #06b6d4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          flex: 1;
          text-align: center;
        }

        .close-btn {
          background: rgba(239, 68, 68, 0.2);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 18px;
        }

        .close-btn:hover {
          background: rgba(239, 68, 68, 0.3);
          transform: scale(1.1);
        }

        .item-details-card {
          margin: 24px;
          background: linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.8));
          border: 1px solid rgba(51, 65, 85, 0.5);
          border-radius: 16px;
          overflow: hidden;
          position: relative;
        }

        .item-details-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: linear-gradient(90deg, #10b981, #06b6d4, #3b82f6);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 16px 20px;
          background: rgba(16, 185, 129, 0.1);
          border-bottom: 1px solid rgba(51, 65, 85, 0.3);
        }

        .card-icon {
          font-size: 1.25rem;
        }

        .card-title {
          color: #10b981;
          margin: 0;
          font-size: 1.125rem;
          font-weight: 600;
        }

        .details-content {
          padding: 20px;
        }

        .detail-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
          padding: 8px 0;
          border-bottom: 1px solid rgba(51, 65, 85, 0.2);
        }

        .detail-row:last-child {
          border-bottom: none;
          margin-bottom: 0;
        }

        .multi-selection-wrapper {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
          margin: 12px 0 4px 0;
        }

        .multi-selection-card {
          background: rgba(15, 23, 42, 0.5);
          border: 1px solid rgba(51, 65, 85, 0.4);
          border-radius: 12px;
          padding: 12px;
          display: flex;
          flex-direction: column;
          gap: 10px;
          transition: border-color 0.2s ease, transform 0.2s ease;
        }

        .multi-selection-card.disabled {
          opacity: 0.5;
        }

        .multi-selection-card:not(.disabled):hover {
          border-color: rgba(16, 185, 129, 0.4);
          transform: translateY(-2px);
        }

        .multi-selection-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 0.9rem;
          color: #e2e8f0;
        }

        .multi-selection-label {
          font-weight: 600;
        }

        .multi-selection-price {
          font-size: 0.8rem;
          color: #10b981;
        }

        .multi-selection-controls {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
        }

        .multi-selection-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: #10b981;
        }

        .detail-label {
          color: #94a3b8;
          font-weight: 600;
          font-size: 0.875rem;
        }

        .detail-value {
          color: #ffffff;
          font-weight: 500;
          text-align: left;
          direction: ltr;
        }

        .price-highlight {
          color: #10b981 !important;
          font-weight: 700;
        }

        .discount-message {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(234, 179, 8, 0.1);
          border: 1px solid rgba(234, 179, 8, 0.3);
          border-radius: 8px;
          padding: 8px 12px;
          color: #facc15;
          font-size: 0.875rem;
          margin: 12px 0;
        }

        .discount-applied {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 8px;
          padding: 8px 12px;
          color: #10b981;
          font-size: 0.875rem;
          margin: 12px 0;
        }

        .discount-icon {
          font-size: 1rem;
        }

        .quantity-control {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
          padding: 8px 0;
        }

        .quantity-buttons {
          display: flex;
          align-items: center;
          gap: 12px;
          direction: ltr;
        }

        .quantity-btn {
          background: linear-gradient(135deg, rgba(51, 65, 85, 0.8), rgba(71, 85, 105, 0.8));
          color: #ffffff;
          border: 1px solid rgba(71, 85, 105, 0.5);
          border-radius: 8px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-weight: 600;
          transition: all 0.2s ease;
        }

        .quantity-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #10b981, #059669);
          border-color: #10b981;
          transform: scale(1.05);
        }

        .quantity-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .quantity-display {
          color: #ffffff;
          font-weight: 700;
          font-size: 1.125rem;
          min-width: 40px;
          text-align: center;
          background: rgba(16, 185, 129, 0.1);
          border: 1px solid rgba(16, 185, 129, 0.3);
          border-radius: 8px;
          padding: 8px;
        }

        .available-quantity {
          color: #94a3b8;
          font-size: 0.75rem;
          margin-top: 4px;
          text-align: center;
          width: 100%;
        }

        .total-section {
          padding: 16px 20px;
          background: rgba(16, 185, 129, 0.05);
          border-top: 1px solid rgba(16, 185, 129, 0.2);
        }

        .total-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .original-price-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
          text-decoration: line-through;
          opacity: 0.7;
        }

        .discount-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 8px;
          color: #10b981;
        }

        .total-label, .original-price-label, .discount-label {
          color: #94a3b8;
          font-weight: 600;
          font-size: 1.125rem;
        }

        .total-amount {
          color: #10b981;
          font-weight: 700;
          font-size: 1.5rem;
          text-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
        }

        .original-price-amount {
          color: #94a3b8;
          font-weight: 600;
          font-size: 1rem;
        }

        .discount-amount {
          color: #10b981;
          font-weight: 700;
          font-size: 1.125rem;
        }

        .order-form {
          padding: 24px;
        }

        .form-group {
          margin-bottom: 16px;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-icon {
          position: absolute;
          left: 12px;
          z-index: 10;
          font-size: 1.125rem;
          color: #94a3b8;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px 12px 44px;
          background: rgba(30, 41, 59, 0.8);
          border: 1px solid rgba(51, 65, 85, 0.5);
          border-radius: 12px;
          color: #ffffff;
          font-size: 1rem;
          text-align: right;
          direction: rtl;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }

        .form-input:focus {
          outline: none;
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
          background: rgba(30, 41, 59, 0.9);
        }

        .form-input::placeholder {
          color: #64748b;
        }

        .error-message {
          display: flex;
          align-items: center;
          gap: 8px;
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          border-radius: 8px;
          padding: 12px;
          color: #f87171;
          font-size: 0.875rem;
          margin-bottom: 16px;
        }

        .error-icon {
          font-size: 1.125rem;
        }

        .form-actions {
          display: flex;
          gap: 12px;
          margin-top: 24px;
        }

        .cancel-btn, .submit-btn {
          flex: 1;
          padding: 14px 20px;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 48px;
        }

        .cancel-btn {
          background: rgba(239, 68, 68, 0.2);
          color: #f87171;
          border: 1px solid rgba(239, 68, 68, 0.3);
        }

        .cancel-btn:hover:not(:disabled) {
          background: rgba(239, 68, 68, 0.3);
          transform: translateY(-1px);
        }

        .submit-btn {
          background: linear-gradient(135deg, #10b981, #059669);
          color: #ffffff;
          position: relative;
          overflow: hidden;
        }

        .submit-btn::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }

        .submit-btn:hover:not(:disabled)::before {
          left: 100%;
        }

        .submit-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #059669, #047857);
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(16, 185, 129, 0.3);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top: 2px solid #ffffff;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .submit-icon {
          font-size: 1.125rem;
        }

        /* Animations */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slideUp {
          from { 
            opacity: 0;
            transform: translateY(30px) scale(0.95);
          }
          to { 
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Responsive Design */
        @media (max-width: 640px) {
          .order-dialog {
            margin: 8px;
            max-width: none;
            border-radius: 16px;
          }

          .dialog-header {
            padding: 20px 16px 12px;
          }

          .dialog-title {
            font-size: 1.25rem;
          }

          .item-details-card {
            margin: 16px;
          }

          .details-content {
            padding: 16px;
          }

          .order-form {
            padding: 16px;
          }

          .form-actions {
            flex-direction: column;
          }

          .detail-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 4px;
          }

          .detail-value {
            text-align: right;
            direction: rtl;
          }

          .quantity-control {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }

        @media (max-width: 480px) {
          .order-overlay {
            padding: 8px;
          }

          .dialog-header {
            padding: 16px 12px 8px;
          }

          .item-details-card {
            margin: 12px;
          }

          .order-form {
            padding: 12px;
          }
        }

        /* Scrollbar Styling */
        .order-dialog::-webkit-scrollbar {
          width: 6px;
        }

        .order-dialog::-webkit-scrollbar-track {
          background: rgba(51, 65, 85, 0.3);
          border-radius: 3px;
        }

        .order-dialog::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #10b981, #06b6d4);
          border-radius: 3px;
        }

        .order-dialog::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #059669, #0891b2);
        }
      `}</style>
    </>
  );
};

export default OrderDialog;